<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter filter-title="Timesheet by Department">
      <oxd-text class="orangehrm-timesheet-help" tag="p">
        Pick the department whose interns you want to report on, then choose
        Single Day or a Date Range. A single day lists every punch; a range
        shows one block per week (Mon–Sun) so long ranges read top-to-bottom
        instead of scrolling sideways. Sub-departments are included.
      </oxd-text>
      <oxd-form @submit-valid="onClickView">
        <oxd-form-row>
          <oxd-grid
            :cols="filters.mode.id === 'range' ? 4 : 3"
            class="orangehrm-full-width-grid"
          >
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.subunit"
                type="select"
                label="Department"
                :options="subunitOptions"
                :rules="rules.subunit"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.mode"
                type="select"
                label="Period"
                :options="modeOptions"
                :clear="false"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="filters.startDate"
                :label="
                  filters.mode.id === 'range'
                    ? 'Start Date'
                    : $t('general.date')
                "
                :rules="rules.startDate"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="filters.mode.id === 'range'">
              <date-input
                v-model="filters.endDate"
                label="End Date"
                :rules="endDateRules"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row v-if="filters.mode.id === 'range'">
          <oxd-input-field
            v-model="filters.includeWeekends"
            type="checkbox"
            :true-value="true"
            :false-value="false"
            option-label="Include weekends"
          />
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <oxd-button
            display-type="secondary"
            :label="$t('general.view')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />

    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container orangehrm-timesheet-toolbar">
        <oxd-text tag="span">{{ headerLabel }}</oxd-text>
        <span class="orangehrm-timesheet-actions">
          <oxd-button
            display-type="secondary"
            label="Download CSV"
            :disabled="items.length === 0"
            @click="onClickDownloadCsv"
          />
          <oxd-button
            display-type="secondary"
            label="Print / PDF"
            :disabled="items.length === 0"
            @click="onClickPrint"
          />
        </span>
      </div>

      <table-header :total="total" :loading="isLoading" :show-divider="false" />

      <!-- Single day: flat list of every punch. -->
      <div v-if="report && report.mode !== 'range'" class="orangehrm-container">
        <oxd-card-table
          :headers="headers"
          :items="items"
          :selectable="false"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>

      <!-- Date range: one pivot block per week, stacked vertically. -->
      <div
        v-else-if="report && report.mode === 'range'"
        class="orangehrm-container"
      >
        <div
          v-for="week in weeks"
          :key="week.label"
          class="orangehrm-week-block"
        >
          <table class="orangehrm-pivot-table">
            <thead>
              <tr>
                <th class="orangehrm-pivot-name orangehrm-week-label">
                  {{ week.label }}
                </th>
                <th
                  v-for="d in week.dates"
                  :key="`dow-${d}`"
                  colspan="3"
                  class="orangehrm-pivot-date"
                >
                  {{ formatDow(d) }}
                </th>
              </tr>
              <tr>
                <th class="orangehrm-pivot-name">Employee</th>
                <th
                  v-for="d in week.dates"
                  :key="`date-${d}`"
                  colspan="3"
                  class="orangehrm-pivot-date"
                >
                  {{ formatDateHeader(d) }}
                </th>
              </tr>
              <tr>
                <th class="orangehrm-pivot-name"></th>
                <template v-for="d in week.dates" :key="`sub-${d}`">
                  <th>In</th>
                  <th>Out</th>
                  <th>Hrs</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="`${week.label}-${row.empNumber}`">
                <td class="orangehrm-pivot-name">{{ row.employeeName }}</td>
                <template
                  v-for="d in week.dates"
                  :key="`${week.label}-${row.empNumber}-${d}`"
                >
                  <td>
                    <template v-if="cellPairs(row, d).length">
                      <div
                        v-for="(p, i) in cellPairs(row, d)"
                        :key="`in-${i}`"
                        :class="{'orangehrm-late': isLate(p.punchIn)}"
                      >
                        {{ p.punchIn }}
                      </div>
                    </template>
                    <span v-else>--:--</span>
                  </td>
                  <td>
                    <template v-if="cellPairs(row, d).length">
                      <div
                        v-for="(p, i) in cellPairs(row, d)"
                        :key="`out-${i}`"
                      >
                        {{ p.punchOut }}
                      </div>
                    </template>
                    <span v-else>--:--</span>
                  </td>
                  <td>
                    <template v-if="cellPairs(row, d).length">
                      <div v-for="(p, i) in cellPairs(row, d)" :key="`hr-${i}`">
                        {{ p.duration }}
                      </div>
                    </template>
                    <span v-else>0.00</span>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {addDays, differenceInCalendarDays, format, parseISO} from 'date-fns';
import {required, validDateFormat} from '@/core/util/validation/rules';
import {freshDate} from '@/core/util/helper/datefns';
import {APIService} from '@/core/util/services/api.service';
import useDateFormat from '@/core/util/composable/useDateFormat';

const modeOptions = [
  {id: 'day', label: 'Single Day'},
  {id: 'range', label: 'Date Range'},
];

// Longest range we render, to keep the report manageable.
const MAX_RANGE_DAYS = 31;

// Punch-ins at or after this clock time are flagged (coloured red).
const LATE_THRESHOLD = '10:00';

export default {
  name: 'SupervisorTimesheetReport',

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/supervisor-timesheet',
    );
    const subunitHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/subunits',
    );
    const {userDateFormat} = useDateFormat();

    const rules = {
      subunit: [required],
      startDate: [required, validDateFormat(userDateFormat)],
    };

    return {
      http,
      subunitHttp,
      rules,
      userDateFormat,
      modeOptions,
    };
  },

  data() {
    return {
      filters: {
        subunit: null,
        mode: modeOptions[0],
        startDate: format(freshDate(), 'yyyy-MM-dd'),
        endDate: format(freshDate(), 'yyyy-MM-dd'),
        includeWeekends: false,
      },
      items: [],
      weeks: [],
      rows: [],
      total: 0,
      isLoading: false,
      report: null,
      subunitOptions: [],
      headers: [
        {name: 'employeeName', title: 'Employee', style: {flex: 1}},
        {name: 'date', title: this.$t('general.date'), style: {flex: 1}},
        {
          name: 'punchIn',
          title: this.$t('attendance.punch_in'),
          style: {flex: 1},
        },
        {
          name: 'punchOut',
          title: this.$t('attendance.punch_out'),
          style: {flex: 1},
        },
        {
          name: 'duration',
          title: this.$t('attendance.duration_hours'),
          style: {flex: 1},
        },
      ],
    };
  },

  computed: {
    headerLabel() {
      if (!this.report) {
        return 'No report generated yet';
      }
      if (this.report.fromDate === this.report.toDate) {
        return `${this.report.departmentName} — ${this.report.fromDate}`;
      }
      return `${this.report.departmentName} — ${this.report.fromDate} to ${this.report.toDate}`;
    },
    // End date must exist, be on/after the start date, and stay within the cap.
    endDateRules() {
      return [
        required,
        validDateFormat(this.userDateFormat),
        (value) => {
          if (!value || !this.filters.startDate) return true;
          return (
            parseISO(value) >= parseISO(this.filters.startDate) ||
            'End date must be on or after the start date'
          );
        },
        (value) => {
          if (!value || !this.filters.startDate) return true;
          const days = differenceInCalendarDays(
            parseISO(value),
            parseISO(this.filters.startDate),
          );
          return (
            days < MAX_RANGE_DAYS ||
            `Date range cannot exceed ${MAX_RANGE_DAYS} days`
          );
        },
      ];
    },
  },

  created() {
    this.subunitHttp.getAll().then((response) => {
      const list = response.data.data ?? [];
      this.subunitOptions = list.map((subunit) => ({
        id: subunit.id,
        name: subunit.name,
        label:
          (subunit.level > 0 ? '  '.repeat(subunit.level) : '') + subunit.name,
      }));
    });
  },

  methods: {
    resolveDateRange() {
      const fromDate = this.filters.startDate;
      const toDate =
        this.filters.mode?.id === 'range' ? this.filters.endDate : fromDate;
      return {fromDate, toDate};
    },
    onClickView() {
      const {fromDate, toDate} = this.resolveDateRange();
      const mode = this.filters.mode?.id ?? 'day';
      const includeWeekends = this.filters.includeWeekends;
      const subunit = this.filters.subunit;
      this.isLoading = true;
      this.http
        .getAll({
          subunitId: subunit?.id,
          fromDate,
          toDate,
        })
        .then((response) => {
          const data = response.data.data ?? [];
          this.items = data
            .map((record) => ({
              id: record.id,
              empNumber: record.empNumber,
              employeeName: record.employeeName,
              date: record.date,
              punchIn: record.punchIn?.time ? record.punchIn.time : '--:--',
              punchOut: record.punchOut?.time ? record.punchOut.time : '--:--',
              duration: record.duration,
            }))
            // Group rows by intern, then chronologically, for a readable sheet.
            .sort((a, b) => {
              const byName = a.employeeName.localeCompare(b.employeeName);
              if (byName !== 0) return byName;
              return `${a.date} ${a.punchIn}`.localeCompare(
                `${b.date} ${b.punchIn}`,
              );
            });
          if (mode === 'range') {
            const built = this.buildWeeks(
              this.items,
              fromDate,
              toDate,
              includeWeekends,
            );
            this.weeks = built.weeks;
            this.rows = built.rows;
          } else {
            this.weeks = [];
            this.rows = [];
          }
          this.total = response.data.meta?.total ?? this.items.length;
          this.report = {
            departmentName: subunit?.name ?? '',
            fromDate,
            toDate,
            mode,
          };
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    // Group records into per-employee rows + weekly buckets (weeks start Monday).
    buildWeeks(records, fromDate, toDate, includeWeekends) {
      const byEmp = {};
      records.forEach((record) => {
        if (!byEmp[record.empNumber]) {
          byEmp[record.empNumber] = {
            empNumber: record.empNumber,
            employeeName: record.employeeName,
            cells: {},
          };
        }
        if (!byEmp[record.empNumber].cells[record.date]) {
          byEmp[record.empNumber].cells[record.date] = [];
        }
        byEmp[record.empNumber].cells[record.date].push({
          punchIn: record.punchIn,
          punchOut: record.punchOut,
          duration: record.duration,
        });
      });
      const rows = Object.values(byEmp).sort((a, b) =>
        a.employeeName.localeCompare(b.employeeName),
      );

      const weeks = [];
      let current = null;
      const end = parseISO(toDate);
      for (let d = parseISO(fromDate); d <= end; d = addDays(d, 1)) {
        const isoDow = Number(format(d, 'i')); // 1 = Mon ... 7 = Sun
        const isWeekend = isoDow === 6 || isoDow === 7;
        if (current === null || isoDow === 1) {
          current = {dates: []};
          weeks.push(current);
        }
        if (includeWeekends || !isWeekend) {
          current.dates.push(format(d, 'yyyy-MM-dd'));
        }
      }
      const nonEmpty = weeks.filter((week) => week.dates.length > 0);
      nonEmpty.forEach((week, index) => {
        week.label = `Week ${index + 1}`;
      });
      return {weeks: nonEmpty, rows};
    },
    cellPairs(row, date) {
      return row.cells[date] ?? [];
    },
    formatDateHeader(date) {
      return format(parseISO(date), 'MMM d');
    },
    formatDow(date) {
      return format(parseISO(date), 'EEEE');
    },
    isLate(time) {
      return (
        typeof time === 'string' && time !== '--:--' && time >= LATE_THRESHOLD
      );
    },
    csvEscape(value) {
      const str = value === null || value === undefined ? '' : String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    },
    onClickDownloadCsv() {
      const isRange = this.report?.mode === 'range';
      const matrix = isRange ? this.buildWeekMatrix() : this.buildFlatMatrix();
      const csv = matrix
        .map((row) => row.map(this.csvEscape).join(','))
        .join('\n');
      // Prepend a BOM so Excel opens UTF-8 content correctly.
      const blob = new Blob(['﻿', csv], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.buildFileName('csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    buildFlatMatrix() {
      const header = [
        'Employee',
        'Date',
        'Punch In',
        'Punch Out',
        'Duration (Hours)',
      ];
      const rows = this.items.map((item) => [
        item.employeeName,
        item.date,
        item.punchIn,
        item.punchOut,
        item.duration,
      ]);
      return [header, ...rows];
    },
    // Weekly CSV: a labelled section per week; multi-punch days stack with newlines.
    buildWeekMatrix() {
      const matrix = [];
      this.weeks.forEach((week) => {
        matrix.push([week.label]);
        const header = ['Employee'];
        week.dates.forEach((d) => {
          const label = `${this.formatDow(d)} ${this.formatDateHeader(d)}`;
          header.push(`${label} In`, `${label} Out`, `${label} Hours`);
        });
        matrix.push(header);
        this.rows.forEach((row) => {
          const cells = [row.employeeName];
          week.dates.forEach((d) => {
            const pairs = this.cellPairs(row, d);
            cells.push(
              pairs.map((p) => p.punchIn).join('\n'),
              pairs.map((p) => p.punchOut).join('\n'),
              pairs.map((p) => p.duration).join('\n'),
            );
          });
          matrix.push(cells);
        });
        matrix.push([]);
      });
      return matrix;
    },
    htmlEscape(value) {
      const str = value === null || value === undefined ? '' : String(value);
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
    lateHtml(time) {
      const escaped = this.htmlEscape(time);
      return this.isLate(time)
        ? `<span style="color:#e4513b;font-weight:600">${escaped}</span>`
        : escaped;
    },
    buildFlatTableHtml() {
      const bodyRows = this.items
        .map(
          (item) =>
            `<tr><td>${this.htmlEscape(item.employeeName)}</td>` +
            `<td>${this.htmlEscape(item.date)}</td>` +
            `<td>${this.lateHtml(item.punchIn)}</td>` +
            `<td>${this.htmlEscape(item.punchOut)}</td>` +
            `<td>${this.htmlEscape(item.duration)}</td></tr>`,
        )
        .join('');
      return (
        '<table><thead><tr>' +
        '<th>Employee</th><th>Date</th><th>Punch In</th>' +
        '<th>Punch Out</th><th>Duration (Hours)</th>' +
        `</tr></thead><tbody>${bodyRows}</tbody></table>`
      );
    },
    buildWeekTableHtml() {
      return this.weeks
        .map((week) => {
          const dowRow =
            `<th>${this.htmlEscape(week.label)}</th>` +
            week.dates
              .map(
                (d) =>
                  `<th colspan="3">${this.htmlEscape(this.formatDow(d))}</th>`,
              )
              .join('');
          const dateRow =
            '<th>Employee</th>' +
            week.dates
              .map(
                (d) =>
                  `<th colspan="3">${this.htmlEscape(
                    this.formatDateHeader(d),
                  )}</th>`,
              )
              .join('');
          const subRow =
            '<th></th>' +
            week.dates
              .map(() => '<th>In</th><th>Out</th><th>Hrs</th>')
              .join('');
          const body = this.rows
            .map((row) => {
              let tds = `<td>${this.htmlEscape(row.employeeName)}</td>`;
              week.dates.forEach((d) => {
                const pairs = this.cellPairs(row, d);
                const inCol = pairs.length
                  ? pairs.map((p) => this.lateHtml(p.punchIn)).join('<br>')
                  : '--:--';
                const outCol = pairs.length
                  ? pairs.map((p) => this.htmlEscape(p.punchOut)).join('<br>')
                  : '--:--';
                const hrCol = pairs.length
                  ? pairs
                      .map((p) => this.htmlEscape(String(p.duration)))
                      .join('<br>')
                  : '0.00';
                tds += `<td>${inCol}</td><td>${outCol}</td><td>${hrCol}</td>`;
              });
              return `<tr>${tds}</tr>`;
            })
            .join('');
          return (
            '<table><thead>' +
            `<tr>${dowRow}</tr><tr>${dateRow}</tr><tr>${subRow}</tr>` +
            `</thead><tbody>${body}</tbody></table>`
          );
        })
        .join('<br>');
    },
    onClickPrint() {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        return;
      }
      const title = this.htmlEscape(this.headerLabel);
      const tableHtml =
        this.report?.mode === 'range'
          ? this.buildWeekTableHtml()
          : this.buildFlatTableHtml();
      printWindow.document.write(
        `<!doctype html><html><head><title>${title}</title>` +
          '<style>' +
          'body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#1c1c1c;}' +
          'h2{font-size:18px;margin-bottom:4px;}' +
          'p{color:#666;margin-top:0;font-size:13px;}' +
          'table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px;}' +
          'th,td{border:1px solid #ccc;padding:6px 10px;text-align:left;vertical-align:top;}' +
          'th{background:#f4f4f4;}' +
          '</style></head><body>' +
          '<h2>Intern Timesheet</h2>' +
          `<p>${title}</p>` +
          tableHtml +
          '</body></html>',
      );
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    },
    buildFileName(extension) {
      const name = this.report
        ? `timesheet_${this.report.fromDate}_to_${this.report.toDate}`
        : 'timesheet';
      return `${name.replace(/[^a-z0-9_-]/gi, '_')}.${extension}`;
    },
  },
};
</script>

<style scoped>
.orangehrm-timesheet-help {
  margin-bottom: 1rem;
  color: var(--oxd-input-field-hint-color, #76777b);
}
.orangehrm-timesheet-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.orangehrm-timesheet-actions {
  display: flex;
  gap: 0.5rem;
}
.orangehrm-week-block {
  overflow-x: auto;
  margin-bottom: 1.5rem;
}
.orangehrm-pivot-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.orangehrm-pivot-table th,
.orangehrm-pivot-table td {
  border: 1px solid var(--oxd-table-divider-color, #e0e0e0);
  padding: 6px 10px;
  text-align: left;
  vertical-align: top;
  white-space: nowrap;
}
.orangehrm-pivot-table th {
  background: var(--oxd-table-header-background-color, #f4f4f4);
  text-align: center;
}
.orangehrm-pivot-table .orangehrm-pivot-name {
  text-align: left;
  position: sticky;
  left: 0;
  background: #fff;
}
.orangehrm-pivot-table thead .orangehrm-pivot-name {
  background: var(--oxd-table-header-background-color, #f4f4f4);
}
.orangehrm-week-label {
  font-weight: 700;
}
.orangehrm-pivot-date {
  text-align: center;
}
.orangehrm-late {
  color: #e4513b;
  font-weight: 600;
}
</style>
